/**
 * Ponto de entrada do widget.
 *
 * O que acontece ao carregar, nesta ordem:
 *
 * 1. Instala os ganchos de console e rede, antes de qualquer outra coisa, para
 *    não perder o erro que aconteceu segundos depois do carregamento.
 * 2. Lê a chave pública do atributo `data-key` da própria tag script.
 * 3. Busca a configuração do projeto na API.
 * 4. Monta a interface dentro de um Shadow DOM.
 *
 * Se qualquer passo falhar, o widget não aparece e o motivo vai para o console
 * com prefixo `[clixy]`. Nunca desenhamos um botão com valores inventados: um
 * botão que não envia é pior que botão nenhum.
 */

import { render } from 'preact';
import { installBuffers } from './buffers';
import { resolveLanguage } from './i18n';
import { buildStyles } from './styles';
import type { BootOptions, RemoteConfig, Reporter } from './types';
import { WidgetApp } from './ui/widget-app';

const HOST_ELEMENT_ID = 'clixy-widget-root';
const SCRIPT_SELECTOR = 'script[data-key]';

installBuffers();

let reporter: Reporter | null = null;
let metadata: Record<string, unknown> | null = null;
let openPanel: (() => void) | null = null;

/**
 * Descobre onde a API mora.
 *
 * O padrão é derivar do endereço do próprio script: quem serve o `w.js` é a
 * mesma instância que recebe o feedback. `data-api` cobre quem serve o arquivo
 * de um CDN em domínio separado.
 */
function resolveBootOptions(): BootOptions | null {
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);

  if (!script) {
    console.error('[clixy] não encontrei a tag script com data-key.');
    return null;
  }

  const key = script.dataset.key?.trim();
  if (!key) {
    console.error('[clixy] o atributo data-key está vazio.');
    return null;
  }

  let assetOrigin: string;
  try {
    assetOrigin = new URL(script.src, window.location.href).origin;
  } catch {
    console.error('[clixy] não consegui determinar de onde o script foi servido.');
    return null;
  }

  const explicitApi = script.dataset.api?.trim();
  return { key, apiUrl: (explicitApi || assetOrigin).replace(/\/$/, ''), assetOrigin };
}

async function fetchConfig({ key, apiUrl }: BootOptions): Promise<RemoteConfig | null> {
  try {
    const response = await fetch(`${apiUrl}/api/v1/config?key=${encodeURIComponent(key)}`);

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as { detail?: string } | null;
      console.error(
        `[clixy] configuração recusada (${response.status}): ${payload?.detail ?? 'sem detalhe'}`,
      );
      return null;
    }

    return (await response.json()) as RemoteConfig;
  } catch (error) {
    console.error('[clixy] não foi possível carregar a configuração.', error);
    return null;
  }
}

function mount(config: RemoteConfig, options: BootOptions): void {
  if (document.getElementById(HOST_ELEMENT_ID)) {
    console.warn('[clixy] o widget já está na página; ignorando a segunda carga.');
    return;
  }

  const { settings } = config;
  const language = resolveLanguage(settings.locale);

  const host = document.createElement('div');
  host.id = HOST_ELEMENT_ID;
  document.body.appendChild(host);

  // `closed` impede que scripts da página alcancem o conteúdo do widget, e o
  // isolamento vale nos dois sentidos: o CSS do cliente também não entra.
  const shadow = host.attachShadow({ mode: 'closed' });
  shadow.host.setAttribute('data-theme', settings.theme);

  const style = document.createElement('style');
  style.textContent = buildStyles(settings);
  shadow.appendChild(style);

  const container = document.createElement('div');
  shadow.appendChild(container);

  render(
    <WidgetApp
      settings={settings}
      language={language}
      publicKey={options.key}
      apiUrl={options.apiUrl}
      assetOrigin={options.assetOrigin}
      getReporter={() => reporter}
      getMetadata={() => metadata}
      registerOpener={(open) => {
        openPanel = open;
      }}
    />,
    container,
  );
}

type Command = 'identify' | 'setMetadata' | 'open';

/**
 * API que o cliente chama da página dele.
 *
 * `window.clixy('identify', { email })` amarra o feedback a um usuário
 * conhecido; `setMetadata` acrescenta contexto próprio (plano, tenant, versão);
 * `open` abre o painel a partir de um botão do próprio produto.
 */
function clixy(command: Command, payload?: unknown): void {
  switch (command) {
    case 'identify':
      reporter = (payload as Reporter | null) ?? null;
      break;
    case 'setMetadata':
      metadata = (payload as Record<string, unknown> | null) ?? null;
      break;
    case 'open':
      if (openPanel) openPanel();
      else console.warn('[clixy] o widget ainda não terminou de carregar.');
      break;
    default:
      console.warn(`[clixy] comando desconhecido: ${String(command)}`);
  }
}

declare global {
  interface Window {
    clixy?: typeof clixy;
  }
}

async function boot(): Promise<void> {
  const options = resolveBootOptions();
  if (!options) return;

  const config = await fetchConfig(options);
  if (!config) return;

  // O corpo pode ainda não existir quando o script roda no `head`.
  if (document.body) {
    mount(config, options);
  } else {
    document.addEventListener('DOMContentLoaded', () => mount(config, options), { once: true });
  }
}

window.clixy = clixy;
void boot();
